/**
 * SystemResources Section Component
 *
 * Displays real-time system resources (memory, workers, database connections).
 * Always shown at top of System Status page with 30s auto-refresh.
 * Includes collapsible Maintenance Schedules subsection.
 */

import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import MemoryIcon from '@mui/icons-material/Memory'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import BuildIcon from '@mui/icons-material/Build'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../../../../services/api'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from '../../../../../components/SystemIcons'

interface ScheduleInfo {
  interval_seconds: number
  next_run: string | null
  last_run: string | null
}

interface SchedulerStats {
  scheduler_running: boolean
  schedules: {
    high: ScheduleInfo
    med: ScheduleInfo
    low: ScheduleInfo
  }
}

interface WorkerData {
  system: string
  scheduler_stats?: SchedulerStats | null
}

interface EnvironmentConfig {
  environment: {
    environment: string
    debug: boolean
    log_level: string
  }
  workers: {
    lightspeed: number
    xero: number
    skimmer: number
    maintenance: number
  }
  auto_sync: {
    enabled: boolean
    stale_days: number
    trigger_limit: number
  }
  sync_limits: {
    max_pending: number
    max_duration_hours: number
  }
  maintenance: {
    skip_connection_checks: boolean
    dry_run: boolean
    cleanup_retention_days: number
    exception_retention_days: number
    vacuum_enabled: boolean
  }
}

const formatMemory = (mb: number): string => {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)}GB`
  }
  return `${Math.round(mb)}MB`
}

const getMemoryStatusColor = (status: string) => {
  switch (status) {
    case 'healthy':
      return '#2e7d32'
    case 'warning':
      return '#ed6c02'
    case 'critical':
      return '#d32f2f'
    default:
      return '#9e9e9e'
  }
}

const getDbConnectionStatus = (percent: number): 'healthy' | 'warning' | 'critical' => {
  if (percent < 60) return 'healthy'
  if (percent < 80) return 'warning'
  return 'critical'
}

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

const formatIntervalCompact = (seconds: number): string => {
  if (seconds >= 3600) {
    const hours = seconds / 3600
    return `${hours}h`
  }
  const minutes = seconds / 60
  return `${minutes}m`
}

const BooleanChip: React.FC<{ value: boolean }> = ({ value }) => (
  <Chip
    label={value ? 'true' : 'false'}
    size="small"
    sx={{
      backgroundColor: value ? '#e8f5e9' : '#f5f5f5',
      color: value ? '#2e7d32' : '#757575',
      fontFamily: 'monospace',
      fontWeight: 500,
      fontSize: '0.7rem',
      height: 20,
    }}
  />
)

const formatRelativeTime = (isoTimestamp: string | null): string => {
  if (!isoTimestamp) return 'Never'

  const now = new Date()
  const timestamp = new Date(isoTimestamp)
  const diffMs = now.getTime() - timestamp.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)

  if (diffSeconds < 0) {
    // Future time (next run)
    const absDiff = Math.abs(diffSeconds)
    if (absDiff < 60) return `in ${absDiff}s`
    if (absDiff < 3600) return `in ${Math.floor(absDiff / 60)}m`
    return `in ${Math.floor(absDiff / 3600)}h ${Math.floor((absDiff % 3600) / 60)}m`
  }

  // Past time (last run)
  if (diffSeconds < 60) return `${diffSeconds}s ago`
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
  return `${Math.floor(diffSeconds / 3600)}h ago`
}

const formatNextRun = (isoTimestamp: string | null): string => {
  if (!isoTimestamp) return 'Unknown'
  return formatRelativeTime(isoTimestamp)
}

const formatLastRun = (isoTimestamp: string | null): string => {
  if (!isoTimestamp) return 'Never'
  return formatRelativeTime(isoTimestamp)
}

const SystemResources: React.FC = () => {
  const [expanded, setExpanded] = useState(true)

  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['health'],
    queryFn: () => apiClient.get('/health/detail'),
    refetchInterval: 30000,
  })

  const { data: workersData, isLoading: workersLoading } = useQuery({
    queryKey: ['admin-workers'],
    queryFn: () => apiClient.get('/admin/workers'),
    refetchInterval: 30000,
  })

  const memoryData = healthData?.data?.memory
  const dbConnections = healthData?.data?.database_connections
  const uptimeSeconds = healthData?.data?.uptime_seconds
  const environmentConfig: EnvironmentConfig | undefined = healthData?.data?.environment_config
  const appVersion: string | undefined = healthData?.data?.version
  const alembicHead: string | undefined = healthData?.data?.alembic_head
  const workersSummary = workersData?.data?.summary
  const workers: WorkerData[] = workersData?.data?.workers || []

  // Find maintenance worker with scheduler stats
  const maintenanceWorker = workers.find(
    (w) => w.system === 'maintenance' && w.scheduler_stats?.scheduler_running
  )
  const schedulerStats = maintenanceWorker?.scheduler_stats

  const containerRss = memoryData?.container_rss_mb || memoryData?.rss_mb || 0
  const containerAllocated = memoryData?.container_allocated_mb || 4096
  const memoryPercent = memoryData?.percent || 0

  const dbTotal = dbConnections?.pg_total_connections || 0
  const dbActive = dbConnections?.pg_active_queries || 0
  const dbMax = dbConnections?.pg_max_connections || 100
  const dbPercent = (dbTotal / dbMax) * 100
  const dbStatus = getDbConnectionStatus(dbPercent)

  if (healthLoading || workersLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MemoryIcon color="primary" />
            <Typography variant="h6" component="h2">
              Runtime Status
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    )
  }

  const scheduleColors: Record<string, string> = {
    HIGH: '#ff9800',
    MED: '#2196f3',
    LOW: '#4caf50',
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoryIcon color="primary" />
            <Typography variant="h6" component="h2">
              Runtime Status
            </Typography>
            <Tooltip title="Auto-refreshing every 30s">
              <Chip
                icon={
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 }
                      }
                    }}
                  />
                }
                label="Live"
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'collapse' : 'expand'}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {/* Row 1: Three metric cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Memory */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h3" fontWeight={600} sx={{ color: getMemoryStatusColor(memoryData?.status || 'unknown') }}>
                  {memoryPercent.toFixed(0)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Memory Used
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={memoryPercent}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    my: 1,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getMemoryStatusColor(memoryData?.status || 'unknown'),
                      borderRadius: 3,
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {formatMemory(containerRss)} / {formatMemory(containerAllocated)}
                </Typography>
              </Box>
            </Grid>

            {/* DB Connections */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h3" fontWeight={600} sx={{ color: getMemoryStatusColor(dbStatus) }}>
                  {dbTotal}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  DB Connections
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={dbPercent}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    my: 1,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getMemoryStatusColor(dbStatus),
                      borderRadius: 3,
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {dbActive} active / {dbMax} max
                </Typography>
              </Box>
            </Grid>

            {/* Uptime */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h3" fontWeight={600} sx={{ color: '#007f98' }}>
                  {uptimeSeconds ? formatUptime(uptimeSeconds) : '--'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Uptime
                </Typography>
                <Box sx={{ height: 6, my: 1 }} /> {/* Spacer to align with other cards */}
                <Typography variant="caption" color="text.secondary">
                  Since restart
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Row 2: Workers + Schedules + Config */}
          <Grid container spacing={2}>
            {/* Workers list */}
            <Grid item xs={12} md={2.5}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  Active Workers
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightspeedIcon size={20} />
                      <Typography variant="body2">Lightspeed</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#0073e6' }}>
                      {workersSummary?.lightspeed?.active || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <XeroIcon size={20} />
                      <Typography variant="body2">Xero</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#13b5ea' }}>
                      {workersSummary?.xero?.active || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SkimmerIcon size={20} />
                      <Typography variant="body2">Skimmer</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1976d2' }}>
                      {workersSummary?.skimmer?.active || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BuildIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                      <Typography variant="body2">Maintenance</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
                      {workersSummary?.maintenance?.active || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            {/* Schedules table - compact */}
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ p: 2, pb: 1 }}>
                  Maintenance Schedules
                </Typography>
                {schedulerStats?.schedules ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Schedule</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Next</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Last</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(['HIGH', 'MED', 'LOW'] as const).map((priority) => {
                          const scheduleKey = priority.toLowerCase() as 'high' | 'med' | 'low'
                          const schedule = schedulerStats.schedules[scheduleKey]
                          return (
                            <TableRow key={priority}>
                              <TableCell sx={{ py: 1 }}>
                                <Chip
                                  label={`${priority} • ${formatIntervalCompact(schedule.interval_seconds)}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: scheduleColors[priority],
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="body2">{formatNextRun(schedule.next_run)}</Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="body2">{formatLastRun(schedule.last_run)}</Typography>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, pt: 0 }}>
                    No scheduler data available
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* System Config table */}
            <Grid item xs={12} md={5.5}>
              <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, height: '100%', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  System Config
                </Typography>
                {environmentConfig ? (
                  <Box sx={{ maxHeight: 160, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.4 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.25, pl: 1 }}>
                      {/* Environment */}
                      <Box sx={{ gridColumn: '1 / -1', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mt: 0.5 }}>
                        Environment
                      </Box>
                      <Box sx={{ pl: 1 }}>Environment</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.environment.environment}</Box>
                      <Box sx={{ pl: 1 }}>Debug</Box>
                      <Box><BooleanChip value={environmentConfig.environment.debug} /></Box>
                      <Box sx={{ pl: 1 }}>Log Level</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.environment.log_level}</Box>
                      <Box sx={{ pl: 1 }}>Version</Box>
                      <Box sx={{ fontWeight: 500 }}>{appVersion || 'unknown'}</Box>
                      <Box sx={{ pl: 1 }}>DB Schema</Box>
                      <Box sx={{ fontWeight: 500 }}>{alembicHead || 'unknown'}</Box>

                      {/* Workers */}
                      <Box sx={{ gridColumn: '1 / -1', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mt: 1 }}>
                        Workers
                      </Box>
                      <Box sx={{ pl: 1 }}>Lightspeed</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.workers.lightspeed}</Box>
                      <Box sx={{ pl: 1 }}>Xero</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.workers.xero}</Box>
                      <Box sx={{ pl: 1 }}>Skimmer</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.workers.skimmer}</Box>
                      <Box sx={{ pl: 1 }}>Maintenance</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.workers.maintenance}</Box>

                      {/* Auto Sync */}
                      <Box sx={{ gridColumn: '1 / -1', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mt: 1 }}>
                        Auto Sync
                      </Box>
                      <Box sx={{ pl: 1 }}>Enabled</Box>
                      <Box><BooleanChip value={environmentConfig.auto_sync.enabled} /></Box>
                      <Box sx={{ pl: 1 }}>Stale Days</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.auto_sync.stale_days}</Box>
                      <Box sx={{ pl: 1 }}>Trigger Limit</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.auto_sync.trigger_limit}</Box>

                      {/* Sync Limits */}
                      <Box sx={{ gridColumn: '1 / -1', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mt: 1 }}>
                        Sync Limits
                      </Box>
                      <Box sx={{ pl: 1 }}>Max Pending</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.sync_limits.max_pending}</Box>
                      <Box sx={{ pl: 1 }}>Max Duration</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.sync_limits.max_duration_hours}h</Box>

                      {/* Maintenance */}
                      <Box sx={{ gridColumn: '1 / -1', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mt: 1 }}>
                        Maintenance
                      </Box>
                      <Box sx={{ pl: 1 }}>Skip Conn Checks</Box>
                      <Box><BooleanChip value={environmentConfig.maintenance.skip_connection_checks} /></Box>
                      <Box sx={{ pl: 1 }}>Dry Run</Box>
                      <Box><BooleanChip value={environmentConfig.maintenance.dry_run} /></Box>
                      <Box sx={{ pl: 1 }}>Cleanup Retention</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.maintenance.cleanup_retention_days}d</Box>
                      <Box sx={{ pl: 1 }}>Exception Retention</Box>
                      <Box sx={{ fontWeight: 500 }}>{environmentConfig.maintenance.exception_retention_days}d</Box>
                      <Box sx={{ pl: 1 }}>Vacuum</Box>
                      <Box><BooleanChip value={environmentConfig.maintenance.vacuum_enabled} /></Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No config data available
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export default SystemResources
